import { Helmet } from "react-helmet";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Link } from "react-router-dom";
import { Shield, UserCheck, FileCheck, Car, Award, CheckCircle } from "lucide-react";

const TransporteursVerifiesMaroc = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Transporteurs Vérifiés France-Maroc – Professionnels de Confiance | GoBledPro</title>
        <meta 
          name="description" 
          content="GoBledPro référence uniquement des transporteurs vérifiés pour le corridor France-Maroc. Découvrez notre processus de sélection et nos critères de qualité." 
        />
        <link rel="canonical" href="https://gobledpro.com/transporteurs-verifies-maroc" />
      </Helmet>

      <Header />

      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <article className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <header className="text-center mb-12">
              <span className="inline-block text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full mb-4">
                Confiance
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
                Transporteurs Vérifiés France – Maroc
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                Comment nous sélectionnons les transporteurs et pourquoi vous pouvez leur faire confiance.
              </p>
            </header>

            {/* Introduction */}
            <section className="prose prose-lg max-w-none mb-12">
              <p className="text-muted-foreground leading-relaxed">
                La qualité de notre service repose sur la qualité des transporteurs que nous référençons. 
                C'est pourquoi nous avons mis en place un processus de vérification rigoureux pour garantir 
                que chaque professionnel présent sur GoBledPro répond à nos standards de fiabilité et de sérieux.
              </p>
            </section>

            {/* Processus de vérification */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Notre processus de vérification
              </h2>
              <div className="space-y-6">
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <UserCheck className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">1. Vérification d'identité</h3>
                      <p className="text-muted-foreground text-sm">
                        Chaque transporteur doit fournir une pièce d'identité valide (carte nationale d'identité ou passeport). 
                        Nous vérifions l'authenticité du document et la correspondance avec les informations déclarées.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <FileCheck className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">2. Documents professionnels</h3>
                      <p className="text-muted-foreground text-sm">
                        Permis de conduire valide, carte grise du véhicule utilisé, et le cas échéant, 
                        licence de transport de marchandises. Ces documents attestent de la légalité de l'activité.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">3. Assurance professionnelle</h3>
                      <p className="text-muted-foreground text-sm">
                        Nous vérifions que le transporteur dispose d'une assurance responsabilité civile professionnelle 
                        et, idéalement, d'une assurance marchandises transportées couvrant les biens de ses clients.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Car className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">4. État du véhicule</h3>
                      <p className="text-muted-foreground text-sm">
                        Le véhicule utilisé doit être en bon état, régulièrement entretenu, et adapté au transport 
                        de colis (espace de chargement propre et sécurisé).
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Award className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">5. Expérience et références</h3>
                      <p className="text-muted-foreground text-sm">
                        Nous privilégions les transporteurs ayant une expérience avérée sur le corridor France-Maroc, 
                        connaissant les procédures douanières et les spécificités de cette liaison.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Critères de qualité */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Nos critères de qualité
              </h2>
              <div className="bg-muted/30 rounded-2xl p-6 md:p-8">
                <p className="text-muted-foreground mb-6">
                  Au-delà des vérifications documentaires, nous évaluons les transporteurs sur des critères 
                  de qualité de service :
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">Réactivité aux demandes</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">Clarté de la communication</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">Respect des délais annoncés</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">Transparence sur les tarifs</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">Soin apporté aux colis</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">Professionnalisme général</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Suivi continu */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Un suivi continu de la qualité
              </h2>
              <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
                <p className="text-muted-foreground mb-6">
                  La vérification ne s'arrête pas à l'inscription. Nous maintenons un suivi continu :
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold flex-shrink-0">1</span>
                    <div>
                      <h4 className="font-medium text-foreground">Collecte des retours clients</h4>
                      <p className="text-muted-foreground text-sm mt-1">
                        Après chaque livraison, les clients peuvent partager leur expérience. 
                        Ces retours nous permettent d'évaluer la qualité du service.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold flex-shrink-0">2</span>
                    <div>
                      <h4 className="font-medium text-foreground">Mise à jour des documents</h4>
                      <p className="text-muted-foreground text-sm mt-1">
                        Les transporteurs doivent maintenir leurs documents à jour (assurance, contrôle technique, etc.).
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold flex-shrink-0">3</span>
                    <div>
                      <h4 className="font-medium text-foreground">Suspension en cas de problème</h4>
                      <p className="text-muted-foreground text-sm mt-1">
                        En cas de plaintes répétées ou de manquements graves, un transporteur peut être 
                        suspendu temporairement ou définitivement de la plateforme.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Ce que cela signifie pour vous */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Ce que cela signifie pour vous
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Sécurité renforcée</h3>
                  <p className="text-muted-foreground text-sm">
                    Vos colis sont confiés à des professionnels vérifiés et assurés.
                  </p>
                </div>
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Tranquillité d'esprit</h3>
                  <p className="text-muted-foreground text-sm">
                    Vous savez que le transporteur a été évalué selon des critères stricts.
                  </p>
                </div>
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <UserCheck className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Recours possible</h3>
                  <p className="text-muted-foreground text-sm">
                    En cas de problème, vous avez un interlocuteur et des informations vérifiées.
                  </p>
                </div>
              </div>
            </section>

            {/* Note importante */}
            <section className="bg-secondary/20 rounded-2xl p-6 md:p-8 mb-12">
              <p className="text-foreground text-sm">
                <strong>Important :</strong> GoBledPro est une plateforme de mise en relation. 
                Nous vérifions les transporteurs pour vous offrir un maximum de garanties, mais le contrat 
                de transport est conclu directement entre vous et le transporteur. Nous recommandons toujours 
                de communiquer clairement sur vos attentes et de confirmer les conditions avant chaque envoi.
              </p>
            </section>

            {/* Liens connexes */}
            <section className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 text-center mb-12">
              <h2 className="text-xl font-heading font-bold text-foreground mb-4">
                En savoir plus
              </h2>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link 
                  to="/transport-colis-maroc-fiable" 
                  className="text-primary hover:underline font-medium"
                >
                  Transport fiable →
                </Link>
                <Link 
                  to="/avis-transport-colis-france-maroc" 
                  className="text-primary hover:underline font-medium"
                >
                  Avis clients →
                </Link>
                <Link 
                  to="/faq-transport-colis-maroc" 
                  className="text-primary hover:underline font-medium"
                >
                  Questions fréquentes →
                </Link>
              </div>
            </section>

            {/* Retour */}
            <div className="pt-8 border-t border-border">
              <Link 
                to="/" 
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                ← Retour à l'accueil
              </Link>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TransporteursVerifiesMaroc;
